// The STL format loses partType. Recognize our handle by its geometry, not by
// choosing the largest part (the handle actually has more faces than the cutter).
// Reference: public/models/cookie_handle_pocket_V2.STL, 11768 triangles.
// These 65 quantiles describe triangle-vertex distances from their centroid,
// normalized by RMS distance: invariant under translation, rotation and scaling.
const HANDLE_FACES = 11768;
const HANDLE_RADII = [
  0.8084558, 0.8085916, 0.8087168, 0.8088735, 0.8090361, 0.8091512,
  0.8094025, 0.8118949, 0.8165244, 0.8246429, 0.8365504, 0.8557984,
  0.8825653, 0.8960858, 0.903023, 0.9066332, 0.9091779, 0.9114704,
  0.9129159, 0.9134917, 0.9175505, 0.9185788, 0.9224365, 0.9242207,
  0.9258604, 0.9285715, 0.931976, 0.9364539, 0.9418265, 0.946355,
  0.9539757, 0.9635045, 0.9706731, 0.9810585, 0.9921791, 1.0072505,
  1.0191171, 1.0324362, 1.0353112, 1.0372854, 1.0392676, 1.0410704,
  1.0424886, 1.0437052, 1.0444537, 1.0448987, 1.0467561, 1.0498902,
  1.0524183, 1.0543114, 1.0554291, 1.0559474, 1.0883527, 1.0886167,
  1.1162259, 1.1164369, 1.1720037, 1.1905021, 1.1961686, 1.1964545,
  1.2162858, 1.2355281, 1.3329661, 1.344358, 1.3592014,
];

function isHandle(positions, faces) {
  if (faces.length !== HANDLE_FACES) return false;
  const center = [0, 0, 0];
  const count = faces.length * 3;
  for (const face of faces) {
    for (let v = face * 9; v < face * 9 + 9; v += 3) {
      for (let axis = 0; axis < 3; axis++) center[axis] += positions[v + axis] / count;
    }
  }
  const radii = new Float64Array(count);
  let n = 0, squaredSum = 0;
  for (const face of faces) {
    for (let v = face * 9; v < face * 9 + 9; v += 3) {
      const r = Math.hypot(positions[v] - center[0], positions[v + 1] - center[1], positions[v + 2] - center[2]);
      radii[n++] = r;
      squaredSum += r * r;
    }
  }
  const rms = Math.sqrt(squaredSum / count);
  if (!rms) return false;
  radii.sort();
  return HANDLE_RADII.every((expected, i) =>
    Math.abs(radii[Math.round(i * (count - 1) / 64)] / rms - expected) < 0.0001
  );
}

/** Measure all body parts together, excluding only recognized separate handles.
 * Input is STLLoader's non-indexed XYZ position array. Never mutates the mesh.
 * Cutter walls, stamp faces, sockets and even tiny disconnected details stay.
 */
export function cookieBodyBounds(positions) {
  const faceCount = positions.length / 9;
  if (!Number.isInteger(faceCount) || !faceCount) return null;
  const parents = Int32Array.from({ length: faceCount }, (_, i) => i);
  function root(i) {
    while (parents[i] !== i) {
      parents[i] = parents[parents[i]];
      i = parents[i];
    }
    return i;
  }
  const vertices = new Map();
  for (let face = 0; face < faceCount; face++) {
    for (let v = face * 9; v < face * 9 + 9; v += 3) {
      const x = positions[v], y = positions[v + 1], z = positions[v + 2];
      if (![x, y, z].every(Number.isFinite)) return null;
      // 0.1 micron welding tolerance accommodates STL float roundoff.
      const key = `${Math.round(x * 10000)},${Math.round(y * 10000)},${Math.round(z * 10000)}`;
      const previous = vertices.get(key);
      if (previous === undefined) vertices.set(key, face);
      else parents[root(face)] = root(previous);
    }
  }
  const components = new Map();
  for (let face = 0; face < faceCount; face++) {
    const id = root(face);
    if (!components.has(id)) components.set(id, []);
    components.get(id).push(face);
  }
  const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
  let excludedHandles = 0;
  for (const faces of components.values()) {
    if (isHandle(positions, faces)) { excludedHandles++; continue; }
    for (const face of faces) {
      for (let v = face * 9; v < face * 9 + 9; v += 3) {
        for (let axis = 0; axis < 3; axis++) {
          min[axis] = Math.min(min[axis], positions[v + axis]);
          max[axis] = Math.max(max[axis], positions[v + axis]);
        }
      }
    }
  }
  if (!Number.isFinite(min[0])) return null;
  return { x: max[0] - min[0], y: max[1] - min[1], z: max[2] - min[2], excludedHandles };
}
